import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import AnimateOnScroll from './AnimateOnScroll.js';
import PageHero from './PageHero.js';
import regionNorthernbc from '../assets/region-northernbc.jpg';

const NorthernBCPage = () => {
  return (
    <div className="bg-custom-earth">
      <div className="wood-container">
        <div className="min-h-screen flex flex-col">
          <Helmet>
            <title>Vegetation Management Northern BC | Boultbee</title>
            <meta name="description" content="Industrial herbicide application in Prince George and the Central Interior. Railroad, industrial, and municipal vegetation management from BC's most experienced applicator." />
            <link rel="canonical" href="https://www.weedmanager.com/service-areas/northern-bc" />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Boultbee Vegetation Management" />
            <meta property="og:title" content="Vegetation Management Northern BC | Boultbee" />
            <meta property="og:description" content="Industrial herbicide application in Prince George and the Central Interior. Railroad, industrial, and municipal vegetation management from BC's most experienced applicator." />
            <meta property="og:url" content="https://www.weedmanager.com/service-areas/northern-bc" />
            <meta property="og:image" content="https://www.weedmanager.com/images/og-northern.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Vegetation Management Northern BC | Boultbee" />
            <meta name="twitter:description" content="Industrial herbicide application in Prince George and the Central Interior. Railroad, industrial, and municipal vegetation management from BC's most experienced applicator." />
            <meta name="twitter:image" content="https://www.weedmanager.com/images/og-northern.jpg" />
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.weedmanager.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Northern BC", "item": "https://www.weedmanager.com/service-areas/northern-bc" }
                ]
              })}
            </script>
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Service",
                "serviceType": "Industrial Vegetation Management",
                "provider": {
                  "@type": "LocalBusiness",
                  "name": "Boultbee Vegetation Management",
                  "url": "https://www.weedmanager.com"
                },
                "areaServed": [
                  { "@type": "City", "name": "Prince George" },
                  { "@type": "City", "name": "Quesnel" },
                  { "@type": "City", "name": "Williams Lake" },
                  { "@type": "City", "name": "Vanderhoof" },
                  { "@type": "City", "name": "Burns Lake" }
                ],
                "description": "Industrial herbicide application in Prince George and the Central Interior for railroad, industrial, and municipal vegetation management."
              })}
            </script>
          </Helmet>

          <PageHero
            title="Prince George & Northern Reach"
            subtitle="Industrial herbicide application from Quesnel to Prince George and beyond"
            backgroundImage={regionNorthernbc}
            darkImage
          />

          <AnimateOnScroll animation="fade-in-up">
            <p className="text-center text-lg md:text-xl font-serif italic text-green-900 max-w-3xl mx-auto px-6 py-6">Boultbee deploys crews from our Okanagan base up to Prince George and surrounding communities — the northern extent of our regular service area.</p>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Prince George & Surrounding Area</h2>
              <p className="text-gray-700">Prince George is the northern extent of our regular deployment range. We service industrial sites, rail corridors, and utility infrastructure in and around the city and communities within about an hour's drive. Our crews travel from the Okanagan equipped for multi-day deployments with the specialized spray equipment that northern industrial sites require.</p>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Industrial & Resource Sector</h2>
              <p className="text-gray-700">The Prince George region's pulp mills, sawmill operations, and industrial infrastructure generate steady demand for vegetation management. Our bare ground programs maintain vegetation-free conditions around mill yards, log sorts, and industrial compounds. We also provide hi-rail herbicide application along rail corridors through the central interior.</p>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Quesnel to Vanderhoof Corridor</h2>
              <p className="text-gray-700">We serve industrial and municipal clients along the Highway 97 corridor from Quesnel through Prince George to Vanderhoof. This stretch includes rail infrastructure, industrial yards, and municipal properties that benefit from the same professional vegetation management programs we deliver across the rest of BC.</p>
            </div>
          </AnimateOnScroll>

          {/* Other Service Areas */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center">Other Regions We Serve</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link to="/service-areas/lower-mainland" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                  <h3 className="font-bold text-gray-900">Lower Mainland</h3>
                  <p className="text-gray-700 text-sm mt-1">Vancouver, Surrey & Fraser Valley</p>
                </Link>
                <Link to="/service-areas/okanagan" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                  <h3 className="font-bold text-gray-900">Okanagan</h3>
                  <p className="text-gray-700 text-sm mt-1">Penticton, Kelowna & Vernon</p>
                </Link>
                <Link to="/service-areas/interior-bc" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                  <h3 className="font-bold text-gray-900">Interior BC</h3>
                  <p className="text-gray-700 text-sm mt-1">Kamloops, Merritt & Revelstoke</p>
                </Link>
              </div>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur text-center">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Get a Quote</h2>
              <p className="text-gray-700 mb-5">Serving Prince George and surrounding communities from our Okanagan base. <Link to="/services" className="text-green-700 underline hover:text-green-900">View all services</Link> or contact us for a site assessment.</p>
              <Link to="/contact" className="cta-button">
                Get a Quote
              </Link>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </div>
  );
};

export default NorthernBCPage;
